import * as React from 'react';
import { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { getDisplayName } from '@klareospace/pharos-lib-utils';
import type { SupportedLanguage } from '@klareospace/pharos-lib-types';

import type { NavigationItem } from '../../../types/navigation';
import { AccessDeniedPage } from '../access-denied-page';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarInset, SidebarProvider } from '../../ui/sidebar';
import { DefaultPendingPage } from '../default-pending-page';
import { SidebarCollapseTrigger } from '../sidebar-collapse-trigger';
import { SidebarNav } from '../sidebar-nav';
import { SidebarUserMenu } from '../sidebar-user-menu';

// ─── Constants ────────────────────────────────────────────────────────────────

/** localStorage key i18next-browser-languagedetector reads on init; written explicitly on language change (see `handleLanguageChange`). */
const I18NEXT_LANGUAGE_STORAGE_KEY = 'i18nextLng';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthenticatedLayoutAuth {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether authentication state is still being determined */
  isLoading: boolean;
  /** Sign out callback */
  signOut: () => Promise<void>;
  /** Authenticated user from the auth provider */
  user: null | { email: string; name: string };
}

export interface AuthenticatedLayoutProps {
  /** Auth state from the app's auth provider */
  auth: AuthenticatedLayoutAuth;
  /** The backend user profile (first/last name for display) — resolved in the app to avoid cross-instance context issues */
  backendUser?: { firstName?: string; lastName?: string } | null;
  /** Optional additional nav items rendered in the sidebar footer area (above user menu) */
  footerNavItems?: NavigationItem[];
  /** Permission check function from the app's PermissionsProvider */
  hasPermission: (permission: string) => boolean;
  /** Whether permissions are still being loaded */
  isPermissionsLoading: boolean;
  /** Supported language codes for the language switcher */
  languages: SupportedLanguage[];
  /** Logo element to render in the sidebar header */
  logo: React.ReactNode;
  /** Top-level navigation items for the sidebar */
  navItems: NavigationItem[];
  /** The permission string required to access the layout (e.g., 'backoffice:access') */
  requiredPermission: string;
  /** Optional element pinned above the top nav items (e.g. a full-width "New" action button) */
  topAction?: React.ReactNode;
  /** Optional banner rendered above the main content area (e.g., impersonation banner) */
  topBanner?: React.ReactNode;
  /** Build label (e.g. `v1.1.0 - 5a17cfb`) shown below sign-out in the user menu. Omitted entirely when absent. */
  versionLabel?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AuthenticatedLayout({
  auth,
  backendUser,
  footerNavItems,
  hasPermission,
  isPermissionsLoading,
  languages,
  logo,
  navItems,
  requiredPermission,
  topAction,
  topBanner,
  versionLabel,
}: AuthenticatedLayoutProps): React.JSX.Element {
  const { isAuthenticated, isLoading, signOut, user } = auth;
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  // Reactive guard: redirect to login when auth state changes to unauthenticated
  // (e.g., after session timeout fires while on an authenticated page).
  // beforeLoad only runs on navigation — this handles in-place auth state changes.
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      void navigate({ search: { redirect: location.pathname }, to: '/login' });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  const handleSignOut = async (): Promise<void> => {
    await signOut();
  };

  /**
   * Persists an explicit language choice so it survives a reload. The detector's `caches` list is
   * empty (see i18n `config.ts` in both web apps) so this is the ONLY path that writes `i18nextLng`
   * — mere detection never does. `try/catch` because storage access can throw in private windows.
   */
  function handleLanguageChange(lang: SupportedLanguage): void {
    void i18n.changeLanguage(lang);
    try {
      localStorage.setItem(I18NEXT_LANGUAGE_STORAGE_KEY, lang);
    } catch {
      // Storage unavailable (e.g. private browsing) — the language still changes for this session.
    }
  }

  if (isLoading || isPermissionsLoading) {
    return <DefaultPendingPage />;
  }

  if (!hasPermission(requiredPermission)) {
    return (
      <AccessDeniedPage
        message={t('auth.accessDenied.message')}
        onSignOut={handleSignOut}
        signOutLabel={t('auth.signOut')}
        title={t('auth.accessDenied.title')}
      />
    );
  }

  const displayName = getDisplayName(backendUser ?? undefined) ?? user?.name;

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader className="min-h-[4.75rem] border-b border-sidebar-border p-4">
          <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center">
            {logo}
            <SidebarCollapseTrigger />
          </div>
        </SidebarHeader>
        <SidebarContent className="px-3 pt-4">
          <SidebarNav
            hasPermission={hasPermission}
            items={navItems}
            renderLink={({ children, path, search }) => (
              <Link search={search} to={path}>
                {children}
              </Link>
            )}
            topAction={topAction}
            translate={t}
          />
        </SidebarContent>
        <SidebarFooter className="border-t border-sidebar-border px-3 py-4">
          {footerNavItems && (
            <SidebarNav
              className="mb-2"
              hasPermission={hasPermission}
              items={footerNavItems}
              renderLink={({ children, path, search }) => (
                <Link search={search} to={path}>
                  {children}
                </Link>
              )}
              translate={t}
            />
          )}
          <SidebarUserMenu
            currentLanguage={languages.find((lang) => lang === i18n.language) ?? languages[0]}
            displayName={displayName}
            email={user?.email}
            languages={languages}
            onLanguageChange={handleLanguageChange}
            onSignOut={handleSignOut}
            versionLabel={versionLabel}
          />
        </SidebarFooter>
      </Sidebar>
      <SidebarInset className="h-svh overflow-hidden">
        {topBanner}
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}
